import { getSelectedAccount } from '@/redux/selectedAccountSlice';
import { showSnackbar } from '@/redux/snackbarSlice';
import React, { Suspense } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import {
  useDeleteBillingMeterOnBuildingMutation,
  useGetBuildingsByAccountQuery,
  useGetBuildingsQuery,
  usePostBillingMeterOnBuildingMutation,
  usePostBuildingMutation,
  useUpdateBuildingMutation,
} from '@/api/recalc/endpoints/buildingApiSlice';

import { useGetAccountsQuery } from '@/api/recalc/endpoints/accountsApiSlice';

import BuildingDataGrid from '@/features/building-optimization/components/datagrid/BuildingDataGrid';
import CreateBuildingDialog from '@/features/building-optimization/components/dialog/CreateBuildingDialog';
import EditBuildingDialog from '@/features/building-optimization/components/dialog/EditBuildingDialog';
import EditBuildingBillingMeterDialog from '@/features/building-optimization/components/dialog/EditBuildingBillingMeterDialog';

import { useGetUnitsByAccountQuery } from '@/api/recalc/endpoints/unitsApiSlice';

import { useLazyGetLocationQuery } from '@/api/dataforsyningen/dataforsyningenApiSlice';

const BuildingPage = () => {
  const account = useSelector(getSelectedAccount);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation(); // <-- get location here

  const [openCreateDialog, setOpenCreateDialog] = React.useState(false);
  const [openEditDialog, setOpenEditDialog] = React.useState(false);
  const [buildingBeingEdited, setBuildingBeingEdited] = React.useState(null);

  const [
    openEditBuildingBillingMetersDialog,
    setOpenEditBuildingBillingMetersDialog,
  ] = React.useState(false);

  const [triggerGetLocation] = useLazyGetLocationQuery();

  const { data: buildings } = useGetBuildingsByAccountQuery(account.AccountId, {
    skip: !account.AccountId || account.AccountId === 1,
  });

  const { data: allBuildings } = useGetBuildingsQuery({
    skip: account.AccountId !== 1,
  });

  const { data: units } = useGetUnitsByAccountQuery(
    buildingBeingEdited?.AccountId,
    {
      skip:
        !buildingBeingEdited?.AccountId || !openEditBuildingBillingMetersDialog,
    },
  );

  const { data: accounts } = useGetAccountsQuery();

  const [postBuilding] = usePostBuildingMutation();
  const [postBillingMeter] = usePostBillingMeterOnBuildingMutation();
  const [deleteBillingMeter] = useDeleteBillingMeterOnBuildingMutation();
  const [updateBuilding] = useUpdateBuildingMutation();

  const onViewBuilding = (building) => {
    navigate(`/PVO/buildings/${encodeURIComponent(building.Name)}`, {
      state: {
        building: building,
        accounts: accounts,
      },
    });
  };

  const handleEditBuilding = async (building) => {
    try {
      const response = await updateBuilding({
        buildingId: buildingBeingEdited.id,
        building,
      }).unwrap();
      dispatch(
        showSnackbar({
          message: 'Building was updated',
          severity: 'success',
          response: response,
        }),
      );
    } catch (error) {
      dispatch(
        showSnackbar({
          message: 'Error updating building',
          severity: 'error',
          response: error,
        }),
      );
    }
  };

  const handleCreateBuilding = async (building) => {
    const payload = {
      ...building,
      AccountId: account.AccountId,
    };

    try {
      const response = await postBuilding(payload).unwrap();
      dispatch(
        showSnackbar({
          message: 'Building was created',
          severity: 'success',
          response: response,
        }),
      );
    } catch (error) {
      dispatch(
        showSnackbar({
          message: 'Error creating building',
          severity: 'error',
          response: error,
        }),
      );
    }
  };

  const handleAddBillingMeter = async (billingMeter) => {
    try {
      const response = await postBillingMeter({
        buildingId: buildingBeingEdited.id,
        billingMeter,
      }).unwrap();
      dispatch(
        showSnackbar({
          message: 'Billing meter was added',
          severity: 'success',
          response: response,
        }),
      );
    } catch (error) {
      dispatch(
        showSnackbar({
          message: 'Error adding billing meter',
          severity: 'error',
          response: error,
        }),
      );
    }
  };

  const handleDeleteBillingMeter = async (billingMeterId) => {
    try {
      const response = await deleteBillingMeter({
        buildingId: buildingBeingEdited.id,
        billingMeterId,
      }).unwrap();
      dispatch(
        showSnackbar({
          message: 'Billing meter was removed',
          severity: 'success',
          response: response,
        }),
      );
    } catch (error) {
      dispatch(
        showSnackbar({
          message: 'Error removing billing meter',
          severity: 'error',
          response: error,
        }),
      );
    }
  };

  const onEditBuilding = (building) => {
    setBuildingBeingEdited(building);
    setOpenEditDialog(true);
  };

  const onEditBillingMeters = (building) => {
    setBuildingBeingEdited(building);
    setOpenEditBuildingBillingMetersDialog(true);
  };

  if (!account.AccountId) return;

  return (
    <>
      <Suspense fallback={<div>Loading...</div>}>
        <BuildingDataGrid
          buildings={account.AccountId === 1 ? allBuildings : buildings}
          accounts={accounts}
          onCreateBuilding={() => setOpenCreateDialog(true)}
          onEditBuilding={onEditBuilding}
          onEditBillingMeters={onEditBillingMeters}
          onViewBuilding={onViewBuilding}
        />
      </Suspense>

      <Suspense fallback={<div>Loading Create Building Dialog...</div>}>
        <CreateBuildingDialog
          open={openCreateDialog}
          onClose={() => setOpenCreateDialog(false)}
          account={account}
          handleCreateBuilding={handleCreateBuilding}
          triggerGetLocation={triggerGetLocation}
        />
      </Suspense>

      {buildingBeingEdited && (
        <Suspense fallback={<div>Loading Edit Dialog...</div>}>
          <EditBuildingDialog
            open={openEditDialog}
            onClose={() => setOpenEditDialog(false)}
            building={buildingBeingEdited}
            handleEditBuilding={handleEditBuilding}
            triggerGetLocation={triggerGetLocation}
          />
        </Suspense>
      )}

      <Suspense fallback={<div>Loading Billing Meters Dialog...</div>}>
        <EditBuildingBillingMeterDialog
          open={openEditBuildingBillingMetersDialog}
          onClose={() => setOpenEditBuildingBillingMetersDialog(false)}
          units={units}
          billingMeters={buildingBeingEdited?.billingMeters || []}
          billingMeterHealthCheck={
            buildingBeingEdited?.state?.BillingMeterHealthCheck
          }
          handleAddBillingMeter={handleAddBillingMeter}
          handleDeleteBillingMeter={handleDeleteBillingMeter}
        />
      </Suspense>

      <Outlet />
    </>
  );
};

export default BuildingPage;
